import AppInfoFooter from "@/components/AppInfoFooter";
import Button from "@/components/Button";
import { EmailInput, PasswordInput } from "@/components/InputFields";
import Link from "@/components/Link";
import Page from "@/components/Page";
import { useLogInMutation } from "@/data/queries";
import { useAuthSessionAtom } from "@/data/store";
import { backendErrorCodeFor } from "@/model/BackendErrors";
import { emailRegex } from "@/model/Constants";
import { VerifyCodePageStateParams } from "@/screens/auth/verify-code/VerifyCodePage";
import cn from "@/util/cn";
import { useForm } from "react-hook-form";
import { Navigate, useLocation, useNavigate } from "react-router";

type LoginPageStateParams = {
  email?: string;
  notice?: string;
};

type LoginPageFormData = {
  email: string;
  password: string;
};

const validateEmail = (email: string) => emailRegex.test(email) || "Please enter a valid email";

const LoginPage = () => {
  const locationState = useLocation().state as LoginPageStateParams;
  const [authSession] = useAuthSessionAtom();
  const navigate = useNavigate();
  const { mutateAsync: logIn, isPending, error: logInError } = useLogInMutation();

  const {
    register,
    handleSubmit,
    trigger: validate,
    getValues,
    formState,
  } = useForm<LoginPageFormData>({
    mode: "onBlur",
    defaultValues: { email: locationState?.email ?? "", password: "" },
  });

  const isLoggedIn = authSession !== null;
  const logInErrorCode = backendErrorCodeFor(logInError);
  const { errors, touchedFields } = formState;

  const onSubmit = async (formData: LoginPageFormData) => {
    const result = await logIn(formData);
    if (result.type === "mfa-challenge") {
      const state: VerifyCodePageStateParams = {
        challenge: result.challenge,
        email: formData.email,
      };
      navigate("/verify-code", { state });
      return;
    }
    navigate("/", { replace: true });
  };

  return (
    <>
      {isLoggedIn && <Navigate to="/" replace={true} />}
      <Page className="bg-gray-50">
        <div className="flex h-full flex-col justify-between">
          <div
            className={cn("mx-auto mt-10 w-full max-w-[480px] rounded-xl bg-white p-12 shadow-2xl")}
          >
            <h2 className="mb-4 text-center text-4xl text-primary/90">
              Welcome to
              <br />
              Bloomlife Connects
            </h2>
            <h3 className="mb-8 text-center text-lg text-primary/90">Sign in to your account</h3>
            {locationState?.notice && !logInErrorCode && (
              <div className="my-4 flex w-full items-center rounded-md border border-blue-200 bg-blue-50 p-4">
                <span className="material-icons me-4 text-blue-600">info</span>
                <p className="text-primary/90">{locationState.notice}</p>
              </div>
            )}
            {logInErrorCode && (
              <div className="my-4 flex w-full items-center rounded-md border border-red-200 bg-red-100 p-4">
                <span className="material-icons me-4 text-red-600">error</span>
                <p className="text-red-600">
                  {logInErrorCode === "USER_DISABLED"
                    ? "This account is inactive, please contact Bloomlife Support"
                    : logInErrorCode === "LOGIN_FAILED"
                      ? "Unknown email/password combination"
                      : logInErrorCode === "MFA_SETUP_REQUIRED"
                        ? "Your account is not set up for two-step verification. Please contact Bloomlife Support."
                        : "Unable to log in"}
                </p>
              </div>
            )}
            <form className="flex flex-col" onSubmit={handleSubmit(onSubmit)}>
              <label htmlFor="email" className="block leading-6 text-primary/90">
                Email address
              </label>
              <EmailInput
                id="email"
                autoComplete="username"
                className="mt-2"
                disabled={isPending}
                {...register("email", {
                  required: "Please enter your email address",
                  validate: validateEmail,
                  onChange: () => {
                    if (touchedFields.email) {
                      validate("email");
                    }
                  },
                })}
              />
              {errors?.email && (
                <p className="mt-2 text-sm italic text-red-600">{errors.email.message}</p>
              )}
              <label htmlFor="password" className="mt-4 block leading-6 text-primary/90">
                Password
              </label>
              <PasswordInput
                id="password"
                required
                className="mt-2"
                autoComplete={"current-password"}
                disabled={isPending}
                {...register("password", {
                  required: "Please enter your password",
                  onChange: () => {
                    if (touchedFields.password) {
                      validate("password");
                    }
                  },
                })}
              />
              {errors?.password && (
                <p className="mt-2 text-sm italic text-red-600">{errors.password.message}</p>
              )}
              <Link
                to="/forgot-password"
                state={{ email: getValues("email") }}
                className="mt-2 self-end"
              >
                Forgot password?
              </Link>
              <Button
                variant="primary"
                type="submit"
                disabled={isPending}
                className="mt-8 w-full text-base"
              >
                Sign in
              </Button>
            </form>
          </div>
          <AppInfoFooter className="mt-auto p-4" />
        </div>
      </Page>
    </>
  );
};

export default LoginPage;
